import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export default function PartnershipModes() {
  const { t } = useLanguage();
  const [activeId, setActiveId] = useState('tech');

  const modes = [
    {
      id: 'tech',
      number: '01',
      icon: 'ri-flask-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      accentBar: 'from-brand-green to-brand-green-dark',
      title: t('技術導入與試產合作', 'Technology Implementation & Pilot Production', '技術導入・試験生産協力'),
      suitable: t('塑膠回收廠、造粒廠、射出押出廠', 'Plastic Recyclers, Compounders, Injection/Extrusion Manufacturers', 'プラスチックリサイクル業者・造粒業者・射出押出メーカー'),
      suitableIcon: 'ri-settings-3-line',
      desc: t(
        '由聚量協助評估原料、配方、添加比例與應用情境，進行小量試產與物性驗證，確保導入成功。',
        'PolyQ assists in evaluating raw materials, formulations, additive ratios, and application scenarios, conducting small-scale pilot production and property verification to ensure successful implementation.',
        '聚量が原料・配合・添加比率・応用シーンの評価を支援し、小規模試験生産と物性検証を実施して導入成功を確保します。'
      ),
      points: [
        { icon: 'ri-customer-service-2-line', text: t('技術諮詢', 'Technical Consultation', '技術相談') },
        { icon: 'ri-test-tube-line', text: t('試產陪跑', 'Pilot Production Support', '試験生産サポート') },
        { icon: 'ri-flask-line', text: t('客製化配方', 'Custom Formulation', 'カスタム配合') },
        { icon: 'ri-bar-chart-line', text: t('物性、穩定性與量產可行性評估', 'Property, Stability & Scalability Assessment', '物性・安定性・量産可能性評価') },
        { icon: 'ri-briefcase-line', text: t('專案型合作導入再生料', 'Project-Based Recycled Material Implementation', 'プロジェクト型再生材料導入') },
      ],
      image: 'https://readdy.ai/api/search-image?query=plastic%20recycling%20factory%20technical%20cooperation%20engineer%20testing%20recycled%20material%20samples%20laboratory%20industrial%20facility%20professional%20B2B%20photography%20green%20accent%20lighting%20clean%20modern%20environment&width=640&height=340&seq=mode-tech-v1&orientation=landscape',
      tagColor: 'text-brand-green bg-brand-green-light',
    },
    {
      id: 'distribution',
      number: '02',
      icon: 'ri-map-pin-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      accentBar: 'from-brand-blue to-brand-blue-dark',
      title: t('區域經銷與策略夥伴', 'Regional Distribution & Strategic Partners', '地域販売代理・戦略的パートナー'),
      suitable: t('具備通路與客戶網絡的經銷商、材料商、產業整合商', 'Distributors, material suppliers, and industry integrators with channel and customer networks', '販路・顧客ネットワークを持つ販売代理店・材料商・産業統合業者'),
      suitableIcon: 'ri-store-line',
      desc: t(
        '由聚量提供產品線與技術背書，合作夥伴負責在地市場拓展與客戶服務，共同開創塑膠再生商機。',
        'PolyQ provides product lines and technical endorsement while partners handle local market development and customer service, jointly creating plastic recycling business opportunities.',
        '聚量が製品ラインと技術的裏付けを提供し、パートナーが地域市場開拓と顧客サービスを担当して、廃プラスチック再生ビジネスチャンスを共に創出します。'
      ),
      points: [
        { icon: 'ri-award-line', text: t('授權銷售 PQernel® / PQMB / rvPlastics', 'Authorized sales of PQernel® / PQMB / rvPlastics', 'PQernel® / PQMB / rvPlasticsの正規販売') },
        { icon: 'ri-file-text-line', text: t('提供技術簡報、樣品包與應用案例資料', 'Technical presentations, sample kits, and application case materials', '技術プレゼン・サンプルキット・応用事例資料の提供') },
        { icon: 'ri-presentation-line', text: t('共同參與客戶簡報、標案與示範場域建置', 'Joint client presentations, tenders, and demonstration site development', '顧客プレゼン・入札・実証サイト構築への共同参加') },
        { icon: 'ri-map-2-line', text: t('可規劃區域獨家或半獨家合作', 'Regional exclusive or semi-exclusive cooperation available', '地域独占または準独占協力の設定可能') },
      ],
      image: 'https://readdy.ai/api/search-image?query=business%20distribution%20partnership%20meeting%20material%20supplier%20regional%20sales%20network%20industrial%20B2B%20professional%20corporate%20photography%20clean%20modern%20office%20blue%20accent%20lighting%20high%20resolution&width=640&height=340&seq=mode-dist-v1&orientation=landscape',
      tagColor: 'text-brand-blue bg-brand-blue-light',
    },
    {
      id: 'esg',
      number: '03',
      icon: 'ri-leaf-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      accentBar: 'from-brand-green to-brand-blue',
      title: t('品牌與 ESG 專案合作', 'Brand & ESG Project Cooperation', 'ブランド・ESGプロジェクト協力'),
      suitable: t('品牌端、通路品牌、企業 ESG 團隊、政府單位', 'Brand owners, channel brands, corporate ESG teams, government agencies', 'ブランドオーナー・流通ブランド・企業ESGチーム・政府機関'),
      suitableIcon: 'ri-government-line',
      desc: t(
        '共同開發低碳產品、回收再生專案、地方循環經濟示範與碳效益資料化方案，讓 ESG 從報告走向產品。',
        'Jointly develop low-carbon products, recycling projects, local circular economy demonstrations, and carbon benefit data solutions — moving ESG from reports to products.',
        '低炭素製品・回収再生プロジェクト・地域サーキュラーエコノミー実証・炭素効果データ化ソリューションを共同開発し、ESGをレポートから製品へと転換します。'
      ),
      points: [
        { icon: 'ri-box-3-line', text: t('低碳商品開發', 'Low-carbon product development', '低炭素製品開発') },
        { icon: 'ri-bookmark-line', text: t('品牌循環故事', 'Brand circular story', 'ブランドサーキュラーストーリー') },
        { icon: 'ri-bar-chart-2-line', text: t('碳足跡、減碳數據與 ESG 報告支援', 'Carbon footprint, reduction data, and ESG report support', 'カーボンフットプリント・削減データ・ESGレポート支援') },
        { icon: 'ri-megaphone-line', text: t('共同參展、技術說明會與 ESG 議題論壇', 'Joint exhibitions, technical seminars, and ESG forums', '共同展示会・技術説明会・ESGフォーラム') },
      ],
      image: 'https://readdy.ai/api/search-image?query=brand%20ESG%20sustainability%20project%20collaboration%20low%20carbon%20product%20development%20circular%20economy%20corporate%20partnership%20professional%20photography%20clean%20modern%20environment%20green%20blue%20accent%20high%20resolution&width=640&height=340&seq=mode-esg-v1&orientation=landscape',
      tagColor: 'text-brand-green bg-brand-green-light',
    },
  ];

  const active = modes.find((m) => m.id === activeId) ?? modes[0];

  return (
    <section className="section-padding bg-[#F5F8FB]">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
            {t('合作模式', 'Cooperation Models', '協力モデル')}
          </p>
          <h2
            className="font-bold text-neutral-dark mb-4"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
          >
            {t(
              '依照你在供應鏈中的位置，聚量提供不同層次的支持與資源',
              'Based on your position in the supply chain, PolyQ provides different levels of support and resources',
              'サプライチェーンにおけるあなたの立場に応じて、聚量は異なるレベルのサポートとリソースを提供します'
            )}
          </h2>
        </div>

        {/* Desktop: tab + detail */}
        <div className="hidden lg:grid grid-cols-[260px_1fr] gap-6">
          <div className="flex flex-col gap-3">
            {modes.map((m) => (
              <button
                key={m.id}
                onClick={() => setActiveId(m.id)}
                className={`flex items-center gap-3 px-4 py-4 rounded-xl text-left transition-all duration-200 cursor-pointer ${activeId === m.id ? 'bg-white border border-brand-green/30 shadow-card' : 'bg-white/60 border border-transparent hover:bg-white hover:border-neutral-border'}`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${activeId === m.id ? m.iconBg : 'bg-neutral-border/40'}`}>
                  <i className={`${m.icon} text-xl ${activeId === m.id ? m.iconColor : 'text-neutral-mid'}`}></i>
                </div>
                <div className="min-w-0">
                  <span className={`text-[0.75rem] font-bold tracking-widest block ${activeId === m.id ? 'text-brand-green' : 'text-neutral-mid'}`}>{m.number}</span>
                  <span className={`text-[1rem] font-semibold leading-tight block ${activeId === m.id ? 'text-neutral-dark' : 'text-neutral-mid'}`}>{m.title}</span>
                </div>
                {activeId === m.id && <i className="ri-arrow-right-s-line text-brand-green text-xl ml-auto flex-shrink-0"></i>}
              </button>
            ))}
          </div>

          <div key={active.id} className="bg-white rounded-card border border-neutral-border overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
            <div className={`h-1 w-full bg-gradient-to-r ${active.accentBar}`} />
            <div className="w-full h-[220px] overflow-hidden">
              <img src={active.image} alt={active.title} className="w-full h-full object-cover object-top" />
            </div>
            <div className="p-7">
              <div className="flex items-start gap-4 mb-5">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${active.iconBg} flex-shrink-0`}>
                  <i className={`${active.icon} ${active.iconColor} text-2xl`}></i>
                </div>
                <div>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[0.875rem] font-semibold mb-1.5 ${active.tagColor}`}>{active.number}</span>
                  <h3 className="font-bold text-neutral-dark" style={{ fontSize: '1.375rem', lineHeight: '1.4' }}>{active.title}</h3>
                </div>
              </div>
              <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-[#F5F8FB] border border-neutral-border mb-5">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`${active.suitableIcon} text-brand-green text-base`}></i>
                </div>
                <div>
                  <span className="text-[0.875rem] font-semibold text-neutral-mid uppercase tracking-wide">
                    {t('適合對象', 'Suitable For', '対象')}
                  </span>
                  <p className="text-neutral-dark text-[1rem] font-medium mt-0.5">{active.suitable}</p>
                </div>
              </div>
              <p className="text-neutral-mid mb-5" style={{ fontSize: '1.0625rem', lineHeight: '1.75' }}>{active.desc}</p>
              <div className="space-y-2.5 mb-6">
                {active.points.map((pt) => (
                  <div key={pt.text} className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-green-light flex-shrink-0 mt-0.5">
                      <i className={`${pt.icon} text-brand-green text-sm`}></i>
                    </div>
                    <span className="text-neutral-dark text-[1rem] leading-relaxed">{pt.text}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-green text-white text-[1.0625rem] font-semibold hover:bg-brand-green-dark transition-all duration-200 whitespace-nowrap cursor-pointer">
                <i className="ri-mail-send-line"></i>
                {t('洽談此合作模式', 'Discuss This Cooperation Model', 'この協力モデルについて相談')}
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile: stacked cards */}
        <div className="lg:hidden flex flex-col gap-6">
          {modes.map((m) => (
            <div key={m.id} className="bg-white rounded-card border border-neutral-border overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
              <div className={`h-1 w-full bg-gradient-to-r ${m.accentBar}`} />
              <div className="w-full h-[180px] overflow-hidden">
                <img src={m.image} alt={m.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${m.iconBg} flex-shrink-0`}>
                    <i className={`${m.icon} ${m.iconColor} text-xl`}></i>
                  </div>
                  <div>
                    <span className={`text-[0.75rem] font-bold tracking-widest block ${m.iconColor}`}>{m.number}</span>
                    <h3 className="font-semibold text-neutral-dark text-[1.0625rem] leading-tight">{m.title}</h3>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F5F8FB] border border-neutral-border mb-3">
                  <i className={`${m.suitableIcon} text-brand-green text-sm mt-0.5 flex-shrink-0`}></i>
                  <p className="text-neutral-dark text-[0.9375rem]">{m.suitable}</p>
                </div>
                <p className="text-neutral-mid text-[1rem] leading-relaxed mb-3">{m.desc}</p>
                <ul className="space-y-2">
                  {m.points.map((pt) => (
                    <li key={pt.text} className="flex items-start gap-2 text-neutral-mid text-[0.9375rem]">
                      <i className="ri-check-line text-brand-green text-sm mt-0.5 flex-shrink-0"></i>
                      {pt.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
